import { TripInfoComponent, type TripPackageDetails } from '../types';
import {
    HotelApiApiHotelHotelIdGet,
    type HotelResponseHotel,
} from '@clients/HotelApi';
import ItineraryOrder from '@itinerary/ItineraryOrder';
import Train from '@itinerary/trip/train/Train';
import TrainPackage from '@itinerary/trip/train/TrainPackage';
import TrainPriceClass from '@itinerary/trip/train/TrainPriceClass';
import TrainPriceType from '@itinerary/trip/train/TrainPriceType';
import TripPackage from '@itinerary/trip/TripPackage';
import TripSegment from '@itinerary/trip/TripSegment';
import { type PassengerCount } from '@itinerary/types';
import { ReserveType } from '@utils/itinerary';
import { TripType } from '@utils/trip';
import logos from '@static/availableTrainLogos.json';
import VerifyResult from '@itinerary/VerifyResult';
import { type SellType, trainSellTypes } from '@utils/train';
import { type AvailableTrain } from '@clients/TrainApi';

export default class TrainTourPackage extends TripPackage<
    Train,
    TrainPriceClass
> {
    override reserveType: ReserveType = ReserveType.Train;

    override get tripPackageDetails(): TripPackageDetails[] {
        return [
            ...super.tripPackageDetails,
            {
                title: 'ایستگاه‌های بین‌راهی',
                component: TripInfoComponent.TrainMidStations,
            },
            {
                title: 'اطلاعات هتل',
                component: TripInfoComponent.HotelInfo,
            },
        ];
    }

    override get label(): string | null {
        return 'تور ویژه سه روز و دو شب';
    }

    override prices: TrainPriceType[] = [];

    hotelName = '';
    hotelId = 0;

    get hotel(): Promise<HotelResponseHotel> {
        const promiseLike = async () => {
            const { data } = await HotelApiApiHotelHotelIdGet({
                hotelId: this.hotelId,
            });
            return data.hotel!;
        };
        return promiseLike();
    }

    private readonly departurePackage: TrainPackage | null = null;
    private readonly returnPackage: TrainPackage | null = null;

    get hotelDescriptions(): string[] {
        return [
            `این رزرو شامل خرید ۲ بلیط قطار با هتل است. بلیط رفت، به همراه یک شب اقامت در ${this.hotelName} در شهر ${this.destination} برای روز اول بوده و بلیط برگشت نیز شامل یک شب اقامت برای روز قبل از حرکت است.`,
            'خدمات تور شامل اقامت با صبحانه می‌باشد.',
            `حداقل تعداد مسافر برای رزرو ۲ بزرگسال یا ۱ بزرگسال به همراه کودک است.`,
            `با توجه به این که هر بلیط، شامل یک شب اقامت در هتل است، استرداد مجزای بلیط و اقامت ممکن نیست و با کنسل کردن هر بلیط، اقامت مربوطه نیز کنسل می‌شود.`,
            `براساس ترکیب مسافران، تعداد و نوع اتاق با هماهنگی شرکت ریلی و هتل، تعیین شده و تخصیص می‌یابد.`,
        ];
    }

    override totalCount = 0;

    override get points() {
        return (
            ((this.departurePoints || 0) + (this.returnPoints || 0)) / 2 || null
        );
    }

    departurePoints: number | null = null;

    returnPoints: number | null = null;

    override segments: TripSegment<Train>[] = [];

    override international = false;

    defaultSellType: SellType | null = null;

    exclusive = false;

    override needsLoginForDiscount = false;

    override key = '';

    override callToReserve = false;

    override minPersons = 2;
    constructor(
        departureTrain: AvailableTrain,
        returnTrain: AvailableTrain,
        totalCount = 0,
        sellTypeCode = 0,
        exclusive = false
    ) {
        super();

        if (!departureTrain || !returnTrain) {
            return;
        }

        this.concreteType = ReserveType.TrainTour;
        this.departurePackage = new TrainPackage(
            departureTrain,
            totalCount,
            sellTypeCode,
            exclusive,
            0,
            2,
            0,
            TripType.RoundTrip
        );
        this.returnPackage = new TrainPackage(
            returnTrain,
            totalCount,
            sellTypeCode,
            exclusive,
            0,
            2,
            0,
            TripType.RoundTrip
        );

        this.totalCount = totalCount;
        const [departurePrice] = departureTrain.prices ?? [];
        const [returnPrice] = returnTrain.prices ?? [];
        const sellType = trainSellTypes.find(
            t => t.code === departurePrice.sellType
        );
        if (!sellType) {
            throw 0;
        }
        const [departureClass] = departurePrice.classes ?? [];
        this.hotelName = departureClass.hotelName ?? '';
        this.hotelId = departureClass.hotelId ?? 0;
        this.extraLogoTitle = departureClass.hotelName ?? '';
        const [returnClass] = returnPrice.classes ?? [];

        this.key = `${departureTrain.id}:${departureClass.id}:${returnTrain.id}:${returnClass.id}`;
        this.uniqueId = `${departureTrain.id}:${departureClass.id}:${returnTrain.id}:${returnClass.id}`;

        this.prices = [
            new TrainPriceType(sellType, [
                new TrainPriceClass(
                    departureClass,
                    departureTrain.isForeign,
                    sellType,
                    exclusive,
                    totalCount
                ),
            ]),
            new TrainPriceType(sellType, [
                new TrainPriceClass(
                    returnClass,
                    departureTrain.isForeign,
                    sellType,
                    exclusive,
                    totalCount
                ),
            ]),
        ];
        this.defaultSellType = sellType;

        this.defaultPrice = null;
        this.selectedPrices = this.prices.map(price => price.classes[0]);
        const [defaultDeparturePrice] = this.prices[0].classes;
        const [defaultReturnPrice] = this.prices[1].classes;
        const departureTrips = [
            new Train(departureTrain, defaultDeparturePrice, logos),
        ];
        const returnTrips = [new Train(returnTrain, defaultReturnPrice, logos)];
        this.segments = [
            new TripSegment(
                departureTrips,
                [...(this.pointOption ? [this.pointOption] : [])],
                '',
                false
            ),
            new TripSegment(
                returnTrips,
                [...(this.pointOption ? [this.pointOption] : [])],
                '',
                false
            ),
        ];
        this.international = !!departureTrain.isForeign;
        this.exclusive = exclusive;
        this.departurePoints = departureTrain.score ?? 0;
        this.returnPoints = returnTrain.score ?? 0;
    }

    override get selectedPrice(): null {
        return null;
    }

    override get capacity(): number {
        return Math.min(
            this.prices[0].classes[0].capacity,
            this.prices[1].classes[0].capacity
        );
    }

    override get singlePayable(): number {
        return (
            this.prices[0].classes[0].singlePayable +
            this.prices[1].classes[0].singlePayable
        );
    }

    override get singlePrice(): number {
        return (
            this.prices[0].classes[0].singlePrice +
            this.prices[1].classes[0].singlePrice
        );
    }

    override checkNeedsRaiseClass() {
        return false;
    }

    override get capacityText(): string {
        return this.capacity.toString();
    }

    override mergedSegment = true;

    override async createBill(count: PassengerCount): Promise<VerifyResult> {
        const departureResult = await this.selectedPrices![0].verify(
            count,
            null
        );
        const returnResult = await this.selectedPrices![1].verify(
            count,
            departureResult.billId
        );
        return this.mergeVerifyResults(departureResult, returnResult);
    }

    private mergeVerifyResults(
        departureRes: VerifyResult,
        returnRes: VerifyResult
    ) {
        const result = new VerifyResult(departureRes.billHandlers[0]);
        departureRes.priceEntries.forEach(value => {
            value.itineraryPackage = this.departurePackage || undefined;
        });
        returnRes.priceEntries.forEach(value => {
            value.itineraryPackage = this.returnPackage || undefined;
        });
        result.reserveId = departureRes.reserveId;
        result.remainingTime =
            departureRes.remainingTime + returnRes.remainingTime;
        result.totalPrice = departureRes.totalPrice + returnRes.totalPrice;
        result.payablePrice =
            departureRes.payablePrice + returnRes.payablePrice;
        result.priceEntries = [
            ...departureRes.priceEntries,
            ...returnRes.priceEntries,
        ];
        result.discount = departureRes.discount + returnRes.discount;
        result.paxList = departureRes.paxList.map((p, index) =>
            p.mergeFields(returnRes.paxList[index])
        );
        result.needsDestinationMobile =
            departureRes.needsDestinationMobile ||
            returnRes.needsDestinationMobile;
        result.billCode = departureRes.billCode;
        result.billId = departureRes.billId;
        result.cancellationTerms = departureRes.cancellationTerms;
        result.departureTime = departureRes.departureTime;
        result.loadDefaultPassenger = departureRes.loadDefaultPassenger;
        result.maxCapacity = this.capacity;
        return result;
    }

    override get isFull() {
        return this.prices.some(
            price => price.classes[0].capacity < this.totalCount
        );
    }

    override get reservable(): boolean {
        if (this.prices.some(price => price.classes[0].capacity === 0)) {
            return false;
        }
        if (
            this.prices.some(
                price => price.classes[0].capacity < this.totalCount
            )
        ) {
            return false;
        }
        // noinspection RedundantIfStatementJS
        if (this.prices.some(price => !price.classes[0].available)) {
            return false;
        }
        return true;
    }

    override get buyText(): string {
        if (this.prices.some(price => price.classes[0].capacity === 0)) {
            return 'تکمیل ظرفیت';
        } else if (
            this.prices.some(
                price => price.classes[0].capacity < this.totalCount
            )
        ) {
            return 'ظرفیت ناکافی';
        } else if (this.prices.some(price => !price.classes[0].available)) {
            return 'قطعی سرور';
        } else if (this.minPersons > this.totalCount) {
            return `حداقل تعداد ${this.minPersons} نفر برای رزرو`;
        }
        return super.buyText;
    }

    override get reserveButtonEnabled() {
        if (this.minPersons > this.totalCount) {
            return false;
        }
        return super.reserveButtonEnabled;
    }

    override get order(): ItineraryOrder {
        return new ItineraryOrder(
            this.departureDateTime.getTime(),
            this.prices?.reduce(
                (prev, curr) => prev + curr.classes[0].singlePayable,
                0
            ),
            a => (a.isTrainTourPackage() ? -1 : 0)
        );
    }

    override get multiSegmentDepartureTitle(): string {
        return `بلیط رفت (${this.origin} به ${this.destination})`;
    }

    override get multiSegmentReturnTitle(): string {
        return `بلیط برگشت (${this.destination} به ${this.origin})`;
    }

    get trainCapacityId(): number | undefined {
        return this.selectedPrices?.[0].id;
    }

    override get loadableTermsUrl(): string | null {
        return this.selectedPrices?.[0].loadableTermsUrl || null;
    }

    override get termsAndConditions(): string | null {
        return this.selectedPrices?.[0].cancellationTerms || null;
    }
}
